import React from "react"
import Dropzone, { FileRejection } from "react-dropzone"
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  IconWithBackground,
  ForwardIcon,
  IconButton,
  CloseIcon,
  createLucideIcon
} from "@carrotfertility/carotene-core"
import { Check } from "lucide-react"
import { compressPhotoIdImage } from "./compressImage"

const CheckIcon = createLucideIcon(Check, "CheckIcon")

export interface PhotoIdImage {
  base64: string
  fileName: string
  contentType: "image/jpeg" | "image/png"
}

interface PhotoIdUploadProps {
  onSubmit: (images: { front: PhotoIdImage; back: PhotoIdImage }) => void | Promise<void>
  submitting?: boolean
}

// Validate the source type, then downscale/re-encode to a small JPEG (see compressImage) so the
// upload fits the BFF's Vercel request-body limit.
async function readAsPhotoIdImage(file: File): Promise<PhotoIdImage> {
  if (file.type !== "image/jpeg" && file.type !== "image/png") {
    throw new Error(`Unsupported image type: ${file.type || "unknown"}`)
  }
  return compressPhotoIdImage(file)
}

const AcceptablePhotoIdFileExtensions = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"]
}

// Cap on the SOURCE file the dropzone accepts (compressed down before upload). Generous so ordinary
// phone photos aren't rejected; the transport-sized cap is enforced post-compression in compressImage.
const MAX_ID_IMAGE_BYTES = 30 * 1024 * 1024 // 30 MB

function UploadDropzone({
  onDropAccepted,
  onDropRejected,
  label,
  helperText,
  accepted,
  onClear,
  removeLabel
}: {
  onDropAccepted: (files: File[]) => void
  onDropRejected: (fileRejections: FileRejection[]) => void
  label: string
  helperText: string
  accepted: boolean
  onClear: () => void
  removeLabel: string
}): JSX.Element {
  return (
    <Dropzone
      multiple={false}
      maxSize={MAX_ID_IMAGE_BYTES}
      onDropAccepted={onDropAccepted}
      onDropRejected={onDropRejected}
      accept={AcceptablePhotoIdFileExtensions}
    >
      {({ getRootProps, getInputProps }) => (
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: theme.tokens.spacing.xs,
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.tokens.borderRadius.md,
            cursor: "pointer",
            textAlign: "center",
            paddingInline: theme.tokens.spacing.lg,
            paddingBlock: theme.tokens.spacing.xl,
            boxSizing: "border-box",
            minBlockSize: "180px",
            minInlineSize: "min(300px, 100%)",
            position: "relative"
          })}
          {...getRootProps()}
        >
          {/* capture="environment" opens the rear camera on mobile */}
          <input {...getInputProps()} capture="environment" />
          {accepted ? (
            <>
              <IconButton
                aria-label={removeLabel}
                size="small"
                onClick={(event) => {
                  event.stopPropagation()
                  onClear()
                }}
                sx={(theme) => ({
                  position: "absolute",
                  insetBlockStart: theme.spacing(theme.tokens.spacing.xs),
                  insetInlineEnd: theme.spacing(theme.tokens.spacing.xs)
                })}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
              <IconWithBackground icon={CheckIcon} variant="square" color="success" size="medium" />
              <Typography variant="h5">{label}</Typography>
            </>
          ) : (
            <>
              <Typography variant="h5">{label}</Typography>
              <Typography variant="body2" color="text.tertiary">
                {helperText}
              </Typography>
              <Button variant="outlined" color="secondary">
                Add photo
              </Button>
            </>
          )}
        </Box>
      )}
    </Dropzone>
  )
}

function Slot({
  side,
  value,
  onPick,
  onClear
}: {
  side: "front" | "back"
  value: PhotoIdImage | null
  onPick: (img: PhotoIdImage) => void
  onClear: () => void
}): JSX.Element {
  const [error, setError] = React.useState<string | null>(null)

  return (
    <Box
      sx={{
        flex: "1 1 16rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      <UploadDropzone
        accepted={value != null}
        label={value ? `${side.charAt(0).toUpperCase() + side.slice(1)} photo added` : `Add ${side} of ID`}
        helperText="Drag and drop or click to upload"
        removeLabel={`Remove ${side} of ID`}
        onClear={() => {
          setError(null)
          onClear()
        }}
        onDropAccepted={async (files) => {
          setError(null)
          const file = files[0]
          if (!file) return
          try {
            onPick(await readAsPhotoIdImage(file))
          } catch {
            setError("Couldn't read that image — please try another file.")
          }
        }}
        onDropRejected={(rejections) => {
          setError(rejections[0]?.errors?.[0]?.message ?? "Unsupported file — use a JPG or PNG image.")
        }}
      />
      {error ? (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      ) : null}
    </Box>
  )
}

export function PhotoIdUpload({ onSubmit, submitting = false }: PhotoIdUploadProps): JSX.Element {
  const theme = useTheme()
  const [front, setFront] = React.useState<PhotoIdImage | null>(null)
  const [back, setBack] = React.useState<PhotoIdImage | null>(null)

  return (
    <Stack spacing={theme.tokens.spacing.xl}>
      <Typography variant="h3" color="text.secondary">
        Upload your identification
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Add a photo of the front and back of a government-issued ID. Your provider uses this to confirm your identity
        before the visit.
      </Typography>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: theme.tokens.spacing.md
        })}
      >
        <Slot side="front" value={front} onPick={setFront} onClear={() => setFront(null)} />
        <Slot side="back" value={back} onPick={setBack} onClear={() => setBack(null)} />
      </Box>
      <Button
        endIcon={<ForwardIcon />}
        variant="contained"
        color="secondary"
        disabled={!front || !back || submitting}
        onClick={() => {
          if (front && back) onSubmit({ front, back })
        }}
        sx={{ alignSelf: "flex-start" }}
      >
        {submitting ? "Uploading…" : "Continue"}
      </Button>
    </Stack>
  )
}
